/**
 * ProfileAvatar - Reusable avatar component with optional upload capability
 * Shows user profile picture or initials fallback
 */
import React, { useState, useRef, useEffect } from 'react';
import { Camera, User, Loader2, X } from 'lucide-react';
import { Button } from '../ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const ProfileAvatar = ({ 
  user, 
  size = 'md', 
  editable = false, 
  onUploadSuccess,
  uploadUserId = 'me',
  className = ''
}) => {
  const [uploading, setUploading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const fileInputRef = useRef(null);

  const profilePicUrl = user?.profile_pic_url;

  // Reset imageError when the URL changes (e.g. after GCS refresh)
  useEffect(() => {
    if (profilePicUrl) {
      setImageError(false);
    }
  }, [profilePicUrl]);

  const sizeClasses = {
    sm: 'h-8 w-8 text-xs',
    md: 'h-12 w-12 text-sm',
    lg: 'h-16 w-16 text-lg',
    xl: 'h-24 w-24 text-2xl',
    '2xl': 'h-32 w-32 text-3xl'
  };

  const getInitials = () => {
    if (!user) return '?';
    const fullName = user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim();
    if (!fullName) return user.email?.[0]?.toUpperCase() || '?';
    return fullName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      alert('Please select a JPEG, PNG, or WebP image');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = sessionStorage.getItem('token');
      const response = await fetch(`${API}/profiles/${uploadUserId}/picture`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        if (onUploadSuccess) {
          onUploadSuccess(data.profile_pic_url);
        }
        setImageError(false);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to upload image');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove this profile picture?')) return;
    setUploading(true);
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${API}/profiles/${uploadUserId}/picture`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setImageError(false);
        if (onUploadSuccess) onUploadSuccess(null);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to remove image');
      }
    } catch (error) {
      console.error('Remove error:', error);
      alert('Failed to remove image');
    } finally {
      setUploading(false);
    }
  };

  const showImage = profilePicUrl && !imageError;

  return (
    <div className={`relative inline-block ${className}`}>
      <div 
        className={`${sizeClasses[size]} rounded-full flex items-center justify-center overflow-hidden ${
          showImage ? '' : 'bg-gradient-to-br from-teal-500 to-teal-700 text-white font-semibold'
        } ${editable ? 'cursor-pointer' : ''}`}
        onClick={editable ? () => fileInputRef.current?.click() : undefined}
        data-testid="profile-avatar"
      >
        {uploading ? (
          <Loader2 className="h-1/2 w-1/2 animate-spin text-white" />
        ) : showImage ? (
          <img 
            // Feb 2026 — DO NOT append `?t=Date.now()` here.  GCS V4
            // signed URLs encode all query params into the signature;
            // adding any extra param invalidates it → the browser
            // gets a 403 from storage.googleapis.com and we have to
            // fall back to the initials gradient.  Cache-busting is
            // already built in because the URL itself changes
            // whenever the backend regenerates it (new X-Goog-Date).
            src={profilePicUrl}
            alt={user?.full_name || 'Profile'} 
            className="h-full w-full object-cover"
            referrerPolicy="no-referrer"
            onError={() => setImageError(true)}
          />
        ) : (
          <span>{getInitials()}</span>
        )}
      </div>

      {editable && !uploading && (
        <>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="absolute bottom-0 right-0 bg-teal-600 hover:bg-teal-700 text-white rounded-full p-1.5 shadow-lg transition-colors"
            title="Change profile picture"
            data-testid="change-avatar-btn"
          >
            <Camera className="h-3 w-3" />
          </button>
          {showImage && (
            <button
              type="button"
              onClick={handleRemove}
              className="absolute top-0 right-0 bg-red-600 hover:bg-red-700 text-white rounded-full p-1 shadow-lg transition-colors"
              title="Remove profile picture"
              data-testid="remove-avatar-btn"
            >
              <X className="h-3 w-3" />
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="avatar-file-input"
          />
        </>
      )}
    </div>
  );
};

export default ProfileAvatar;
